import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { ImageUpload } from '@/shared/components/ui/image-upload'
import { Loader2, Check, X } from 'lucide-react'
import { uploadFile } from '@/shared/services/uploads'
import type { GroupCreateInput, GroupExtensionUpdateInput, GroupExtensionListItem } from '@/modules/users/types/group'

/** Paleta Tailwind: color principal (500) + variaciones */
const TAILWIND_COLORS: Record<string, Record<string, string>> = {
  slate:   { '300': '#cbd5e1', '400': '#94a3b8', '500': '#64748b', '600': '#475569', '700': '#334155' },
  gray:    { '300': '#d1d5db', '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151' },
  red:     { '300': '#fca5a5', '400': '#f87171', '500': '#ef4444', '600': '#dc2626', '700': '#b91c1c' },
  orange:  { '300': '#fdba74', '400': '#fb923c', '500': '#f97316', '600': '#ea580c', '700': '#c2410c' },
  amber:   { '300': '#fcd34d', '400': '#fbbf24', '500': '#f59e0b', '600': '#d97706', '700': '#b45309' },
  yellow:  { '300': '#fde047', '400': '#facc15', '500': '#eab308', '600': '#ca8a04', '700': '#a16207' },
  lime:    { '300': '#bef264', '400': '#a3e635', '500': '#84cc16', '600': '#65a30d', '700': '#4d7c0f' },
  green:   { '300': '#86efac', '400': '#4ade80', '500': '#22c55e', '600': '#16a34a', '700': '#15803d' },
  emerald: { '300': '#6ee7b7', '400': '#34d399', '500': '#10b981', '600': '#059669', '700': '#047857' },
  teal:    { '300': '#5eead4', '400': '#2dd4bf', '500': '#14b8a6', '600': '#0d9488', '700': '#0f766e' },
  cyan:    { '300': '#67e8f9', '400': '#22d3ee', '500': '#06b6d4', '600': '#0891b2', '700': '#0e7490' },
  sky:     { '300': '#7dd3fc', '400': '#38bdf8', '500': '#0ea5e9', '600': '#0284c7', '700': '#0369a1' },
  blue:    { '300': '#93c5fd', '400': '#60a5fa', '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8' },
  indigo:  { '300': '#a5b4fc', '400': '#818cf8', '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca' },
  violet:  { '300': '#c4b5fd', '400': '#a78bfa', '500': '#8b5cf6', '600': '#7c3aed', '700': '#6d28d9' },
  purple:  { '300': '#d8b4fe', '400': '#c084fc', '500': '#a855f7', '600': '#9333ea', '700': '#7e22ce' },
  fuchsia: { '300': '#f0abfc', '400': '#e879f9', '500': '#d946ef', '600': '#c026d3', '700': '#a21caf' },
  pink:    { '300': '#f9a8d4', '400': '#f472b6', '500': '#ec4899', '600': '#db2777', '700': '#be185d' },
  rose:    { '300': '#fda4af', '400': '#fb7185', '500': '#f43f5e', '600': '#e11d48', '700': '#be123c' },
}

// Schema base con name opcional para compatibilidad de tipos
const groupFormSchema = z.object({
  name: z.string().max(150, 'Maximo 150 caracteres').optional(),
  permiso: z.boolean().optional(),
  laboratorio: z.boolean().optional(),
  cliente: z.boolean().optional(),
  imagen_url: z.string().optional().or(z.literal('')),
  color_nombre: z.string().max(20).optional().or(z.literal('')),
  color_tono: z.string().max(3).optional().or(z.literal('')),
})

type GroupFormData = z.infer<typeof groupFormSchema>

interface GroupFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: GroupCreateInput) => void
  onUpdate?: (extensionId: number, data: GroupExtensionUpdateInput) => void
  isSubmitting: boolean
  defaultLaboratorio?: boolean
  title?: string
  hideLaboratorioField?: boolean
  mode?: 'create' | 'edit'
  editData?: GroupExtensionListItem | null
}

export function GroupFormDialog({
  open,
  onOpenChange,
  onSubmit,
  onUpdate,
  isSubmitting,
  defaultLaboratorio = false,
  title,
  hideLaboratorioField = false,
  mode = 'create',
  editData = null,
}: GroupFormDialogProps) {
  const isEditMode = mode === 'edit' && editData !== null
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedColorFamily, setSelectedColorFamily] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<GroupFormData>({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      name: '',
      permiso: false,
      laboratorio: defaultLaboratorio,
      cliente: false,
      imagen_url: '',
      color_nombre: '',
      color_tono: '500',
    },
  })

  const permiso = watch('permiso')
  const laboratorio = watch('laboratorio')
  const cliente = watch('cliente')
  const colorNombre = watch('color_nombre')
  const colorTono = watch('color_tono')

  // Reset form when dialog opens/closes or editData changes
  useEffect(() => {
    if (open && isEditMode && editData) {
      // Pre-llenar formulario con datos existentes
      reset({
        name: editData.grupo_nombre,
        permiso: editData.permiso,
        laboratorio: editData.laboratorio,
        cliente: editData.cliente,
        imagen_url: editData.imagen_url || '',
        color_nombre: editData.color_nombre || '',
        color_tono: editData.color_tono || '500',
      })
      setImageUrl(editData.imagen_url || null)
      setPendingFile(null)
      setUploadError(null)
      setSelectedColorFamily(editData.color_nombre || null)
    } else if (!open) {
      reset({
        name: '',
        permiso: false,
        laboratorio: defaultLaboratorio,
        cliente: false,
        imagen_url: '',
        color_nombre: '',
        color_tono: '500',
      })
      setImageUrl(null)
      setPendingFile(null)
      setUploadError(null)
      setSelectedColorFamily(null)
    }
  }, [open, reset, defaultLaboratorio, isEditMode, editData])

  const handleFormSubmit = async (data: GroupFormData) => {
    setUploadError(null)
    let finalImageUrl = imageUrl

    // Si hay un archivo pendiente, subirlo primero
    if (pendingFile) {
      setIsUploading(true)
      try {
        // Usar nombre del laboratorio para el archivo
        const labName = isEditMode ? editData?.grupo_nombre : data.name
        const uploadResponse = await uploadFile(pendingFile, 'app/laboratorios', labName || undefined)
        finalImageUrl = uploadResponse.url
      } catch (error) {
        setUploadError('Error al subir la imagen. Intenta de nuevo.')
        setIsUploading(false)
        return
      }
      setIsUploading(false)
    }

    if (isEditMode && editData && onUpdate) {
      const updateData: GroupExtensionUpdateInput = {
        permiso: data.permiso ?? false,
        laboratorio: data.laboratorio ?? false,
        cliente: data.cliente ?? false,
        imagen_url: finalImageUrl || null,
        color_nombre: data.color_nombre || null,
        color_tono: data.color_tono || null,
      }
      onUpdate(editData.id, updateData)
    } else {
      if (!data.name || !data.name.trim()) {
        return // El nombre es requerido en modo creacion
      }
      const createData: GroupCreateInput = {
        name: data.name.trim(),
        permiso: data.permiso ?? false,
        laboratorio: data.laboratorio ?? false,
        cliente: data.cliente ?? false,
        imagen_url: finalImageUrl || null,
        color_nombre: data.color_nombre || null,
        color_tono: data.color_tono || null,
      }
      onSubmit(createData)
    }
  }

  const handleImageChange = (file: File | null, previewUrl: string | null) => {
    setPendingFile(file)
    if (file) {
      // Hay un archivo nuevo pendiente
      setImageUrl(previewUrl)
    } else {
      // Se elimino la imagen
      setImageUrl(null)
    }
    setValue('imagen_url', previewUrl || '')
    setUploadError(null)
  }

  const dialogTitle = title || (isEditMode ? 'Editar Grupo' : 'Crear Nuevo Grupo')
  const dialogDescription = isEditMode
    ? 'Modifique la configuracion del grupo. El nombre del grupo no puede ser cambiado.'
    : 'Complete la informacion del nuevo grupo. La extension se creara automaticamente.'
  const submitButtonText = isEditMode ? 'Guardar Cambios' : 'Crear Grupo'

  const isProcessing = isSubmitting || isUploading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 overflow-y-auto flex-1 pr-1">
          {/* Name - Solo visible en modo creacion */}
          {!isEditMode ? (
            <div className="space-y-2">
              <Label htmlFor="name">
                Nombre del Grupo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Ej: Administradores, Laboratorio_XYZ"
                disabled={isProcessing}
                autoFocus
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              <p className="text-xs text-muted-foreground">
                El nombre debe ser unico. Use guiones bajos en lugar de espacios.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Nombre del Grupo</Label>
              <Input
                value={editData?.grupo_nombre || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                El nombre del grupo no puede ser modificado.
              </p>
            </div>
          )}

          {/* Imagen Upload */}
          <div className="space-y-2">
            <Label>Imagen del Laboratorio</Label>
            <ImageUpload
              value={imageUrl || undefined}
              onChange={handleImageChange}
              disabled={isProcessing}
              maxSize={10}
            />
            {uploadError && (
              <p className="text-sm text-destructive">{uploadError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Logo o imagen del laboratorio (opcional). Formatos: PNG, JPG, GIF, WebP. Max 10MB.
              La imagen se subira al guardar.
            </p>
          </div>

          {/* Color picker Tailwind */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Color del Laboratorio</Label>
              {colorNombre && (
                <button
                  type="button"
                  onClick={() => {
                    setValue('color_nombre', '')
                    setValue('color_tono', '500')
                    setSelectedColorFamily(null)
                  }}
                  disabled={isProcessing}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                  Quitar
                </button>
              )}
            </div>

            {/* Paso 1: Colores principales (500) */}
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(TAILWIND_COLORS).map(([family, shades]) => {
                const isActive = selectedColorFamily === family
                return (
                  <button
                    key={family}
                    type="button"
                    disabled={isProcessing}
                    onClick={() => {
                      setSelectedColorFamily(isActive ? null : family)
                      if (!isActive) {
                        // Al seleccionar familia, poner 500 por defecto
                        setValue('color_nombre', family)
                        setValue('color_tono', '500')
                      }
                    }}
                    className={`h-7 w-7 rounded-md border-2 transition-all flex items-center justify-center ${
                      isActive
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: shades['500'] }}
                    title={family}
                  >
                    {isActive && <Check className="h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]" />}
                  </button>
                )
              })}
            </div>

            {/* Paso 2: Variaciones del color seleccionado */}
            {selectedColorFamily && TAILWIND_COLORS[selectedColorFamily] && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground capitalize">{selectedColorFamily} — variaciones</p>
                <div className="flex gap-1.5">
                  {Object.entries(TAILWIND_COLORS[selectedColorFamily]).map(([shade, hex]) => {
                    const isActive = colorTono === shade
                    return (
                      <button
                        key={shade}
                        type="button"
                        disabled={isProcessing}
                        onClick={() => setValue('color_tono', shade)}
                        className={`flex flex-col items-center gap-0.5`}
                      >
                        <div
                          className={`h-8 w-10 rounded-md border-2 transition-all flex items-center justify-center ${
                            isActive
                              ? 'border-foreground scale-105'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: hex }}
                        >
                          {isActive && <Check className="h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]" />}
                        </div>
                        <span className="text-[10px] text-muted-foreground">{shade}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {colorNombre && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className={`h-4 w-4 rounded border bg-${colorNombre}-${colorTono || '500'}`} />
                <span className="font-mono">{colorNombre}-{colorTono || '500'}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Color usado en calendario y áreas asociadas (opcional).
            </p>
          </div>

          {/* Switches */}
          <div className="space-y-4 rounded-lg border p-4">
            <h4 className="text-sm font-medium">Configuracion</h4>

            {/* Mostrar switch de laboratorio: siempre en modo edicion, o si no esta oculto en modo creacion */}
            {(isEditMode || !hideLaboratorioField) && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="laboratorio">Asociado a Laboratorio</Label>
                  <p className="text-sm text-muted-foreground">
                    {laboratorio
                      ? 'Este grupo representa un laboratorio farmaceutico'
                      : 'Marcar si este grupo debe asociarse a un laboratorio'}
                  </p>
                </div>
                <Switch
                  id="laboratorio"
                  checked={laboratorio}
                  onCheckedChange={(checked) => {
                    setValue('laboratorio', checked)
                    if (!checked) setValue('cliente', false)
                  }}
                  disabled={isProcessing}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
            )}

            {laboratorio && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="cliente">Cliente Activo</Label>
                  <p className="text-sm text-muted-foreground">
                    Marcar si este laboratorio es un cliente activo
                  </p>
                </div>
                <Switch
                  id="cliente"
                  checked={cliente}
                  onCheckedChange={(checked) => setValue('cliente', checked)}
                  disabled={isProcessing}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="permiso">Permiso Especial</Label>
                <p className="text-sm text-muted-foreground">
                  Otorgar permisos especiales a este grupo
                </p>
              </div>
              <Switch
                id="permiso"
                checked={permiso}
                onCheckedChange={(checked) => setValue('permiso', checked)}
                disabled={isProcessing}
                className="data-[state=checked]:bg-teal-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isProcessing} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUploading ? 'Subiendo imagen...' : submitButtonText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
